import React, { useState, useCallback } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import Header from './components/Header';
import Sidebar from './components/Sidebar';
import Overview from './pages/Overview';
import Analytics from './pages/Analytics';
import FeeRules from './pages/FeeRules';
import LocationGroups from './pages/LocationGroups';
import CarrierGroups from './pages/CarrierGroups';
import AddRule from './pages/AddRule';
import BulkEdit from './pages/BulkEdit';
import RuleHealth from './pages/RuleHealth';
import TestRules from './pages/TestRules';
import ChangeLog from './pages/ChangeLog';
import UploadFile from './pages/UploadFile';
import Approvals from './pages/Approvals';
import PushToServer from './pages/PushToServer';
import RawEditor from './pages/RawEditor';
import AiRuleChat from './pages/AiRuleChat';
import AgentManagement from './pages/AgentManagement';
import Login from './pages/Login';

import { RuleProvider } from './context/RuleContext';

const theme = createTheme({
  palette: {
    primary: { main: '#1976d2' },
    secondary: { main: '#f50057' },
    background: { default: '#f5f7fa', paper: '#ffffff' },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', borderRadius: 8 },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: { borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' },
      },
    },
    MuiInputLabel: {
      defaultProps: {
        shrink: true,
      },
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          fontWeight: 500,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        notchedOutline: {
          borderColor: '#e0e0e0',
          '& legend': { maxWidth: '100%' },
        },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined' },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: { marginTop: 4, fontSize: '0.75rem' },
      },
    },
  },
});

function App() {
  const [currentPage, setCurrentPage] = useState(() => {
    return localStorage.getItem('gfs_current_page') || 'overview';
  });
  const [editingRule, setEditingRule] = useState(null);
  const [isCopying, setIsCopying] = useState(false);
  const [autoRunAiAnalysis, setAutoRunAiAnalysis] = useState(false);
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('gfs_user');
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });

  const handleLogin = (userData) => {
    setUser(userData);
    localStorage.setItem('gfs_user', JSON.stringify(userData));
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('gfs_user');
  };

  const handleNavigate = useCallback((page) => {
    setCurrentPage(page);
    localStorage.setItem('gfs_current_page', page);
    setEditingRule(null);
    setIsCopying(false);
  }, []);

  const handleEditRule = useCallback((rule) => {
    setEditingRule(rule);
    setIsCopying(false);
    setCurrentPage('add-rule');
  }, []);

  const handleCopyRule = useCallback((rule) => {
    setEditingRule(rule);
    setIsCopying(true);
    setCurrentPage('add-rule');
  }, []);

  // Show login if not authenticated
  if (!user) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Login onLogin={handleLogin} />
        <ToastContainer position="bottom-right" autoClose={3000} />
      </ThemeProvider>
    );
  }

  const renderPage = () => {
    switch (currentPage) {
      case 'overview': return <Overview />;
      case 'analytics': return <Analytics autoRunAi={autoRunAiAnalysis} onAiDone={() => setAutoRunAiAnalysis(false)} />;
      case 'rules': return <FeeRules onEditRule={handleEditRule} onCopyRule={handleCopyRule} />;
      case 'locations': return <LocationGroups />;
      case 'carriers': return <CarrierGroups />;
      case 'add-rule': return <AddRule editingRule={editingRule} isCopying={isCopying} onDone={() => { setEditingRule(null); setIsCopying(false); setCurrentPage('rules'); }} />;
      case 'bulk-edit': return <BulkEdit />;
      case 'rule-health': return <RuleHealth />;
      case 'test-rules': return <TestRules />;
      case 'changelog': return <ChangeLog />;
      case 'upload': return <UploadFile user={user} />;
      case 'approvals': return <Approvals user={user} />;
      case 'push-server': return <PushToServer user={user} />;
      case 'ai-assistant': return <AiRuleChat onNavigate={handleNavigate} onGoToAnalysis={() => { setAutoRunAiAnalysis(true); handleNavigate('analytics'); }} />;
      case 'raw-editor': return <RawEditor user={user} />;
      case 'agent-management': return <AgentManagement />;
      default: return <Overview />;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <RuleProvider user={user}>
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
          <Sidebar currentPage={currentPage} onNavigate={handleNavigate} user={user} />
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Header onNavigate={handleNavigate} user={user} onLogout={handleLogout} />
            {user?.role === 'AGENT' && (
              <Box sx={{ bgcolor: '#e3f2fd', px: 3, py: 1, borderBottom: '1px solid #90caf9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span style={{ fontSize: 16 }}>📝</span>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>
                    Agent Mode — You are editing a local copy. Changes are NOT saved until you submit for admin approval.
                  </span>
                </Box>
              </Box>
            )}
            <Box component="main" sx={{ flex: 1, p: 3, overflow: 'auto' }}>
              {renderPage()}
            </Box>
          </Box>
        </Box>
        <ToastContainer position="bottom-right" autoClose={3000} />
      </RuleProvider>
    </ThemeProvider>
  );
}

export default App;
